import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import styles from './CostCenterProvisioning.module.scss'

import { EnterpriseMember } from '@/api/generated'
import { getEnterpriseMembers } from '@/api/internal'
import Input from '@/components/Input'
import { Spinner } from '@/components/Spinner'

const DEBOUNCE_MS = 300
const RESULT_LIMIT = 10
const SEARCH_INPUT_ID = 'cost-center-provisioning-member-search'

export const memberDisplayName = (member: EnterpriseMember): string => {
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ')
  return name || member.email || member.userId
}

interface MemberSearchProps {
  enterpriseId: string
  disabled?: boolean
  onSelect: (member: EnterpriseMember) => void
  label?: string
  hint?: string
  inputId?: string
}

const MemberSearch = ({
  enterpriseId,
  disabled = false,
  onSelect,
  label = 'Billing owner',
  hint = 'Search members by name or email. Invoices go to this person.',
  inputId = SEARCH_INPUT_ID,
}: MemberSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [dismissed, setDismissed] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (searchQuery.length < 2) {
      setDebouncedQuery('')
      return
    }

    const timeoutId = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      setDismissed(false)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const {
    data: members = [],
    isFetching,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ['costCenterProvisioning', 'memberSearch', enterpriseId, debouncedQuery],
    queryFn: async () => {
      const response = await getEnterpriseMembers(enterpriseId, { search: debouncedQuery, limit: RESULT_LIMIT })
      return response.data.members
    },
    enabled: debouncedQuery.length >= 2,
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setDismissed(true)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (member: EnterpriseMember) => {
    setSearchQuery('')
    setDebouncedQuery('')
    onSelect(member)
  }

  const showResults = isSuccess && !dismissed

  return (
    <div ref={searchContainerRef} className={styles.searchContainer}>
      <label className={styles.fieldLabel} htmlFor={inputId}>
        {label}
      </label>
      <span className={styles.fieldHint}>{hint}</span>
      <div className={styles.searchInputWrapper}>
        <Input
          id={inputId}
          type="text"
          className={styles.searchInput}
          placeholder="Type to search members..."
          value={searchQuery}
          onInput={setSearchQuery}
          isDisabled={disabled}
        />
        {isFetching && (
          <div className={styles.searchSpinner}>
            <Spinner size={16} color="gray" />
          </div>
        )}
      </div>
      {isError && <p className={styles.fieldError}>Failed to search members. Please try again.</p>}
      {showResults && members.length > 0 && (
        <div className={styles.searchResults} role="listbox">
          {members.map((member) => (
            <button
              key={member.userId}
              type="button"
              role="option"
              className={styles.searchResultItem}
              onClick={() => handleSelect(member)}
            >
              <span className={styles.searchResultName}>{memberDisplayName(member)}</span>
              {member.email && <span className={styles.searchResultEmail}>{member.email}</span>}
            </button>
          ))}
        </div>
      )}
      {showResults && members.length === 0 && !isFetching && (
        <div className={styles.searchResults}>
          <div className={styles.noResults}>No members found</div>
        </div>
      )}
    </div>
  )
}

export default MemberSearch
